/**
 * ProprScout Image Search
 * Main orchestrator component for image geolocation analysis
 */

import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Search, Upload, AlertCircle, Coins } from 'lucide-react';
import axios from 'axios';

// Detective components
import {
  UploadModal,
  SearchingOverlay,
  ResultsSidebar,
  LazyMapView,
  StreetViewComparison
} from './detective';
import API_BASE_URL from '../config/api';

const API_URL = API_BASE_URL;

export default function PropertyDetective() {
  const { t } = useTranslation();
  const { user, token, isAuthenticated } = useAuth();

  // UI State
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showStreetView, setShowStreetView] = useState(false);
  const [viewMode, setViewMode] = useState('map'); // 'map' | 'street'

  // Data State
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [filename, setFilename] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [quota, setQuota] = useState(null);

  // Fetch quota on mount
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchQuota();
    }
  }, [isAuthenticated, token]);

  const fetchQuota = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/detective/quota`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setQuota(response.data);
    } catch (err) {
      console.error('Failed to fetch quota:', err);
      // Set default quota for non-authenticated users
      setQuota({
        remaining: user?.credits?.balance || 0,
        limit: 15,
        subscription: user?.subscription?.type || 'free'
      });
    }
  };

  const handleUploadSubmit = useCallback(async ({ files, filename: name, confirmed_authorized, confirmed_property_use }) => {
    if (!isAuthenticated) {
      setError(t('detective.error'));
      return;
    }

    setShowUploadModal(false);
    setIsSearching(true);
    setError(null);
    setFilename(name);

    // Create preview from first image
    const firstFile = files[0];
    const preview = URL.createObjectURL(firstFile);
    setImagePreview(preview);

    try {
      const formData = new FormData();

      // Append all images
      files.forEach((file, index) => {
        formData.append(`images`, file); // Backend expects 'images' array
      });

      // GDPR: Include consent flags
      formData.append('confirmed_authorized', confirmed_authorized);
      formData.append('confirmed_property_use', confirmed_property_use);
      formData.append('image_count', files.length);

      const response = await axios.post(
        `${API_URL}/api/detective/analyze`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const data = response.data;

      // CRITICAL: Detect GeoCLIP service failures (fallback/mock data)
      // Check if we got fallback data (confidence = 0 or very low, coordinates at 0,0 or fallback location)
      const isFallbackData = (
        data.confidence <= 0.2 || // Fallback confidence threshold
        (data.coordinates?.lat === 0 && data.coordinates?.lon === 0) || // No coordinates
        (Math.abs(data.coordinates?.lat - 38.7223) < 0.001 && Math.abs(data.coordinates?.lon - (-9.1393)) < 0.001) // Lisbon fallback
      );

      if (isFallbackData) {
        throw {
          response: {
            status: 503,
            data: {
              error: 'SERVICE_UNAVAILABLE',
              message: 'Starting Analysis Service',
              details: {
                reason: 'Our AI is warming up. This takes about a minute on the first use.',
                confidence: data.confidence,
                isServiceIssue: true
              },
              suggestions: [
                'Wait 60 seconds and try again',
                'No credits deducted',
                'Contact support if this continues'
              ]
            }
          }
        };
      }

      // Add quality indicators
      let sourceDescription = 'General Estimate';
      if (data.best_prediction?.source === 'ocr_text') sourceDescription = 'Text Verification (Exact)';
      else if (data.best_prediction?.source === 'retrieval_cluster') sourceDescription = 'Visual Match (Precise)';
      else if (data.best_prediction?.source === 'coarse_geoclip') sourceDescription = 'AI Region Estimate (Approximate)';
      else if (data.best_prediction?.snapped_to_building) sourceDescription = 'Building Footprint Match';

      const qualityResult = {
        ...data,
        quality: {
          confidence: data.confidence,
          confidenceLevel: data.confidence > 0.7 ? 'high' : data.confidence > 0.4 ? 'medium' : 'low',
          warning: data.confidence < 0.4 ? t('detective.lowConfidenceWarning') : null,
          source: sourceDescription
        }
      };

      setResult(qualityResult);
      setUploadedImage(data.image_url || preview);
      setShowResults(true);

      // Refresh quota
      fetchQuota();
    } catch (err) {
      console.error('Analysis failed:', err);
      console.error('Error response:', err.response);
      console.error('Error data:', err.response?.data);
      console.error('Error status:', err.response?.status);
      console.error('Error headers:', err.response?.headers);

      const errorData = err.response?.data;
      const errorMessage = errorData?.message ||
        errorData?.error ||
        err.message ||
        t('detective.error');

      // Log for debugging
      console.log('Parsed error message:', errorMessage);
      console.log('Error type detected:', errorData?.error);

      // === SERVICE UNAVAILABLE (GeoCLIP down/waking up) ===
      if (errorData?.error === 'SERVICE_UNAVAILABLE' || err.response?.status === 503) {
        setError({
          type: 'service_unavailable',
          message: errorData?.message || '🔧 AI Service Temporarily Unavailable',
          details: errorData?.details || {
            reason: 'Our geolocation service is warming up. Please wait a moment and try again.',
            isServiceIssue: true
          },
          suggestions: errorData?.suggestions || [
            'Wait 30-60 seconds for the service to start',
            'Try uploading your photo again',
            'Your credits were not deducted'
          ]
        });
      }
      // === CRITICAL: Handle LOW_CONFIDENCE error from backend ===
      else if (errorData?.error === 'LOW_CONFIDENCE') {
        setError({
          type: 'low_confidence',
          message: errorData.message,
          details: errorData.details,
          suggestions: errorData.details?.suggestions || []
        });
      }
      // Check for quota error
      else if (err.response?.status === 403 && errorData?.error === 'quota_exceeded') {
        setError({
          type: 'quota',
          message: t('detective.noCredits')
        });
      }
      // Check for GDPR/consent errors
      else if (errorData?.error === 'consent_required' || errorData?.error === 'terms_not_accepted') {
        setError({
          type: 'consent',
          message: errorMessage,
          details: errorData
        });
      }
      // Network/timeout errors
      else if (err.code === 'ECONNABORTED' || err.code === '  ETIMEDOUT') {
        setError({
          type: 'network',
          message: 'Request timed out. The analysis is taking longer than expected. Please try again.'
        });
      }
      // CORS or network errors
      else if (!err.response) {
        setError({
          type: 'network',
          message: 'Network error. Please check your internet connection and try again.'
        });
      }
      // Generic error with better feedback
      else {
        setError({
          type: 'analysis',
          message: errorMessage,
          details: {
            status: err.response?.status,
            statusText: err.response?.statusText
          }
        });
      }
    } finally {
      setIsSearching(false);
    }
  }, [token, isAuthenticated]);

  const handleDownloadPDF = async () => {
    if (!result || !result.analysisId) {
      alert(t('errors.generic'));
      return;
    }

    try {
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await axios.get(
        `${API_URL}/api/detective/export/${result.analysisId}`,
        { headers }
      );

      if (response.data.success && response.data.data.html) {
        // Create a new window with the HTML content
        const printWindow = window.open('', '_blank');
        printWindow.document.write(response.data.data.html);
        printWindow.document.close();

        // Give time for images to load, then trigger print dialog
        setTimeout(() => {
          printWindow.print();
        }, 1000);
      } else {
        throw new Error('Failed to generate report');
      }
    } catch (error) {
      console.error('PDF export failed:', error);
      alert(t('errors.generic'));
    }
  };

  const handleSearchCity = (city) => {
    // TODO: Navigate to property search with city filter
    // TODO: Navigate to property search with city filter
    if (city) {
      window.open(`https://www.google.com/maps/search/properties+in+${encodeURIComponent(city)}`, '_blank');
    }
  };

  const handleNewSearch = () => {
    // Reset state for new search
    setResult(null);
    setShowResults(false);
    setShowStreetView(false);
    setError(null);

    // Clean up preview URL
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    setUploadedImage(null);
    setFilename('');

    // Open upload modal
    setShowUploadModal(true);
  };

  const handleCloseResults = () => {
    setShowResults(false);
  };

  const handleStreetView = () => {
    setShowStreetView(true);
    setShowResults(false);
  };

  const handleBackFromStreetView = () => {
    setShowStreetView(false);
    setShowResults(true);
  };

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="relative h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-[#00d185]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Search className="w-8 h-8 text-[#00d185]" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">{t('detective.title')}</h2>
          <p className="text-gray-400 mb-6">
            {t('detective.loginPrompt')}
          </p>
          <a
            href="/login"
            className="inline-block bg-[#00d185] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#00b574] transition-colors"
          >
            {t('auth.login')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="relative h-screen bg-[#0a0a0a] overflow-hidden">
        {/* Map (always visible in background) - lazy loaded for better initial bundle size */}
        <LazyMapView
          center={result?.coordinates}
          showRadius={!!result}
          confidenceRadius={25000}
          viewMode={viewMode === '3d' ? '3d' : 'map'}
          markerImageUrl={result ? uploadedImage : null}
        />


        {/* View Mode Toggle (only when results) */}
        {result && !showStreetView && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20">
            <div className="bg-[#1a1a1a] rounded-lg border border-[#2a2a2a] p-1 flex">
              <button
                onClick={() => setShowStreetView(true)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${showStreetView
                  ? 'bg-white text-black'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                {t('detective.streetView')}
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'map' && !showStreetView
                  ? 'bg-white text-black'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                {t('detective.mapView')}
              </button>
            </div>
          </div>
        )}

        {/* Credits Display */}
        <div className="absolute top-4 right-4 z-20">
          <div className="bg-[#1a1a1a] rounded-lg border border-[#2a2a2a] px-4 py-2 flex items-center gap-2">
            <Coins size={16} className="text-[#00d185]" />
            <span className="text-gray-400 text-sm">{t('credits.balance')}: </span>
            <span className="text-white font-medium">
              {user?.credits?.balance ?? 0}
            </span>
          </div>
        </div>

        {/* Upload Modal */}
        <UploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onSubmit={handleUploadSubmit}
        />

        {/* Searching Overlay */}
        <SearchingOverlay
          isVisible={isSearching}
          imagePreview={imagePreview}
        />

        {/* Results Sidebar */}
        <ResultsSidebar
          isOpen={showResults}
          onClose={handleCloseResults}
          result={result}
          imageUrl={uploadedImage}
          filename={filename}
          onSearchCity={handleSearchCity}
          onDownloadPDF={handleDownloadPDF}
          onStreetView={handleStreetView}
        />

        {/* Street View Comparison */}
        <StreetViewComparison
          coordinates={result?.coordinates}
          uploadedImage={uploadedImage}
          onBack={handleBackFromStreetView}
          isVisible={showStreetView}
        />

        {/* Error Toast - Enhanced for LOW_CONFIDENCE and SERVICE_UNAVAILABLE */}
        {error && (
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-50 max-w-2xl w-full px-4">
            <div className={`rounded-lg shadow-2xl border-2 ${error.type === 'quota'
              ? 'bg-yellow-500/95 border-yellow-600 text-black'
              : error.type === 'low_confidence'
                ? 'bg-orange-500/95 border-orange-600 text-white'
                : error.type === 'service_unavailable'
                  ? 'bg-blue-500/95 border-blue-600 text-white'
                  : error.type === 'consent'
                    ? 'bg-purple-500/95 border-purple-600 text-white'
                    : 'bg-red-500/95 border-red-600 text-white'
              }`}>

              {/* Service Unavailable - Friendly, helpful message */}
              {error.type === 'service_unavailable' ? (
                <div className="p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="text-3xl">🔧</div>
                    <div>
                      <h3 className="font-bold text-xl mb-2">{error.message}</h3>
                      <p className="text-sm opacity-95 mb-3 leading-relaxed">
                        {error.details?.reason || 'Our AI geolocation service is temporarily warming up. This is totally normal!'}
                      </p>

                      {error.details?.confidence !== undefined && (
                        <div className="bg-white/20 rounded-lg px-3 py-2 mb-3 text-sm">
                          <span className="opacity-80">Service Status:</span> <span className="font-mono font-bold">Initializing...</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {error.suggestions && error.suggestions.length > 0 && (
                    <div className="mb-4 pl-12">
                      <p className="font-semibold mb-2 flex items-center gap-2">
                        <span>💡</span> What to do:
                      </p>
                      <ul className="list-none space-y-2 text-sm opacity-95">
                        {error.suggestions.map((suggestion, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-white/60">•</span>
                            <span>{suggestion}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex items-center gap-3 pl-12">
                    <button
                      onClick={() => setError(null)}
                      className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-medium transition-colors"
                    >
                      Got It
                    </button>
                    <button
                      onClick={handleNewSearch}
                      className="px-4 py-2 bg-white text-blue-600 hover:bg-gray-100 rounded-lg font-bold transition-colors flex items-center gap-2"
                    >
                      <Upload size={16} />
                      Try Again
                    </button>
                  </div>
                </div>
              ) : error.type === 'low_confidence' ? (
                /* Low Confidence - Show detailed suggestions */
                <div className="p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <AlertCircle size={24} className="flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold text-lg mb-2">{error.message}</h3>
                      {error.details?.reason && (
                        <p className="text-sm opacity-90 mb-3">{error.details.reason}</p>
                      )}
                      {error.details?.confidence !== undefined && (
                        <p className="text-sm opacity-90 mb-3">
                          Confidence: {(error.details.confidence * 100).toFixed(1)}%
                          (minimum required: {(error.details.threshold * 100).toFixed(0)}%)
                        </p>
                      )}
                    </div>
                  </div>

                  {error.suggestions && error.suggestions.length > 0 && (
                    <div className="mb-4 pl-9">
                      <p className="font-semibold mb-2">💡 Suggestions:</p>
                      <ul className="list-disc list-inside space-y-1 text-sm opacity-90">
                        {error.suggestions.map((suggestion, idx) => (
                          <li key={idx}>{suggestion}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex items-center gap-3 pl-9">
                    <button
                      onClick={() => setError(null)}
                      className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded font-medium transition-colors"
                    >
                      Close
                    </button>
                    <button
                      onClick={handleNewSearch}
                      className="px-4 py-2 bg-white text-orange-600 hover:bg-gray-100 rounded font-medium transition-colors"
                    >
                      Try Another Photo
                    </button>
                  </div>
                </div>
              ) : (
                /* Standard error display */
                <div className="flex items-center gap-3 px-6 py-4">
                  <AlertCircle size={20} />
                  <span>{error.message}</span>
                  <button
                    onClick={() => setError(null)}
                    className="ml-auto font-medium hover:underline"
                  >
                    {t('common.close')}
                  </button>
                  {error.type === 'quota' && (
                    <a
                      href="/pricing"
                      className="ml-2 font-medium underline"
                    >
                      {t('credits.buyMore')}
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Initial state - prompt to upload */}
        {!result && !isSearching && !showUploadModal && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            <div className="bg-[#1a1a1a]/90 backdrop-blur-sm rounded-xl border border-[#2a2a2a] p-8 text-center max-w-md pointer-events-auto">
              <div className="w-16 h-16 bg-[#00d185]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Upload className="w-8 h-8 text-[#00d185]" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">
                {t('detective.title')}
              </h2>
              <p className="text-gray-400 mb-6">
                {t('detective.subtitle')}
              </p>
              <button
                onClick={() => setShowUploadModal(true)}
                className="bg-[#00d185] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#00b574] transition-colors"
              >
                {t('detective.uploadPhoto')}
              </button>
            </div>
          </div>
        )}
      </div >
    </>
  );
}
